import { useMemo } from 'react'
import './RunSummaryStatistics.css'

interface RunAgent {
  agent_id: string
  agent_name: string
}

interface RunResult {
  status: string
  cost: number
  time: number
  agent_id: string | null
  agent_name: string | null
}

interface RunSummaryStatisticsProps {
  successRate: number | null
  avgCost: number | null
  avgTime: number | null
  totalQueued: number
  completedCount: number
  agents?: RunAgent[]
  results?: RunResult[]
}

interface AgentStats {
  agent_name: string
  completedCount: number
  passCount: number
  dsqCount: number
  successRate: number | null
  avgCost: number | null
  avgTime: number | null
}

function getProgressClass(rate: number): string {
  if (rate >= 70) return 'high'
  if (rate >= 40) return 'medium'
  return 'low'
}

function CheckCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--color-pass)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="10" r="8" />
      <path d="M6.5 10l2.5 2.5 5-5" />
    </svg>
  )
}

function CostIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="10" r="8" />
      <path d="M10 5v10M7.5 7.5h3.75a1.25 1.25 0 010 2.5H7.5M7.5 10h4.25a1.25 1.25 0 010 2.5H7.5" />
    </svg>
  )
}

function TimerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="10" cy="11" r="7" />
      <path d="M10 7v4l2.5 1.5M8 2h4" />
    </svg>
  )
}

function QueueIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="14" height="3" rx="1" />
      <rect x="3" y="8.5" width="14" height="3" rx="1" />
      <rect x="3" y="13" width="14" height="3" rx="1" />
    </svg>
  )
}

function RunSummaryStatistics({ successRate, avgCost, avgTime, totalQueued, completedCount, agents, results }: RunSummaryStatisticsProps) {
  const isMultiAgent = agents && agents.length > 1

  const agentStats = useMemo((): AgentStats[] => {
    if (!isMultiAgent || !results) return []
    return agents.map(agent => {
      const agentResults = results.filter(r => r.agent_id === agent.agent_id)
      const completed = agentResults.filter(r => r.status === 'pass' || r.status === 'fail' || r.status === 'dsq' || r.status === 'disagree')
      const passCount = completed.filter(r => r.status === 'pass').length
      const dsqCount = completed.filter(r => r.status === 'dsq').length
      const eligible = completed.length - dsqCount
      return {
        agent_name: agent.agent_name,
        completedCount: completed.length,
        passCount,
        dsqCount,
        successRate: eligible > 0 ? (passCount / eligible) * 100 : null,
        avgCost: completed.length > 0 ? completed.reduce((s, r) => s + r.cost, 0) / completed.length : null,
        avgTime: completed.length > 0 ? completed.reduce((s, r) => s + r.time, 0) / completed.length : null,
      }
    })
  }, [isMultiAgent, agents, results])

  return (
    <div data-testid="run-summary-statistics" className="run-summary-statistics">
      <h2 className="run-summary-title">Summary Statistics</h2>
      <div className="run-summary-cards">
        <div className="run-summary-card" data-testid="stat-progress">
          <div className="run-summary-card-header">
            <span className="run-summary-card-label">Progress:</span>
            <span className="run-summary-card-icon"><QueueIcon /></span>
          </div>
          <div className="run-summary-card-value">
            {completedCount} / {totalQueued} tests completed
          </div>
          {totalQueued > 0 && (
            <div className="run-summary-progress-bar">
              <div
                className={`run-summary-progress-fill ${completedCount === totalQueued ? 'high' : 'medium'}`}
                style={{ width: `${Math.min((completedCount / totalQueued) * 100, 100)}%` }}
              />
            </div>
          )}
        </div>

        <div className="run-summary-card" data-testid="stat-success-rate">
          <div className="run-summary-card-header">
            <span className="run-summary-card-label">Aggregated Success Rate:</span>
            <span className="run-summary-card-icon"><CheckCircleIcon /></span>
          </div>
          <div className="run-summary-card-value">
            {successRate !== null ? `${Math.round(successRate)}%` : 'N/A'}
          </div>
          {successRate !== null && (
            <div className="run-summary-progress-bar">
              <div
                className={`run-summary-progress-fill ${getProgressClass(successRate)}`}
                style={{ width: `${Math.min(successRate, 100)}%` }}
              />
            </div>
          )}
        </div>

        <div className="run-summary-card" data-testid="stat-avg-cost">
          <div className="run-summary-card-header">
            <span className="run-summary-card-label">{isMultiAgent ? 'Overall Avg Cost:' : 'Average Cost:'}</span>
            <span className="run-summary-card-icon"><CostIcon /></span>
          </div>
          <div className="run-summary-card-value">
            {avgCost !== null ? `$${avgCost.toFixed(2)} per failure` : 'N/A'}
          </div>
        </div>

        <div className="run-summary-card" data-testid="stat-avg-time">
          <div className="run-summary-card-header">
            <span className="run-summary-card-label">{isMultiAgent ? 'Overall Avg Time:' : 'Average Time:'}</span>
            <span className="run-summary-card-icon"><TimerIcon /></span>
          </div>
          <div className="run-summary-card-value">
            {avgTime !== null ? `${Math.round(avgTime)}s per failure` : 'N/A'}
          </div>
        </div>
      </div>

      {isMultiAgent && agentStats.length > 0 && (
        <div className="run-summary-agent-breakdown" data-testid="agent-breakdown">
          <h3 className="run-summary-subtitle">Per-Agent Breakdown</h3>
          <table className="run-summary-agent-table">
            <thead>
              <tr>
                <th>Agent</th>
                <th>Completed</th>
                <th>Success Rate</th>
                <th>Avg Cost</th>
                <th>Avg Time</th>
              </tr>
            </thead>
            <tbody>
              {agentStats.map(stat => (
                <tr key={stat.agent_name} data-testid={`agent-stat-${stat.agent_name}`}>
                  <td><span className="run-summary-agent-name">{stat.agent_name}</span></td>
                  <td>{stat.completedCount} ({stat.passCount} pass{stat.dsqCount > 0 ? `, ${stat.dsqCount} dsq` : ''})</td>
                  <td>
                    {stat.successRate !== null ? (
                      <span className={`run-summary-rate ${getProgressClass(stat.successRate)}`}>
                        {Math.round(stat.successRate)}%
                      </span>
                    ) : 'N/A'}
                  </td>
                  <td>{stat.avgCost !== null ? `$${stat.avgCost.toFixed(2)}` : 'N/A'}</td>
                  <td>{stat.avgTime !== null ? `${Math.round(stat.avgTime)}s` : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default RunSummaryStatistics
